/**
 * Anonymous pre-consent funnel beacon.
 *
 * PostHog loads nothing until analytics consent is granted, so a NEW visitor —
 * the one who just arrived from a social or store link — is invisible for the
 * whole first visit. This posts a single allowlisted word per milestone to
 * `/api/funnel`, which counts it as {step, source, day, hour} and nothing else.
 *
 * It stores NOTHING on the device: no cookie, no localStorage, no
 * sessionStorage write, no identifier of any kind. Consent is not required for
 * the same reason it is not required for /go — nothing here is read from or
 * written to the visitor's device, and nothing recorded can be tied back to
 * them. Keep it that way: this file must never gain a visitor id.
 */
(function () {
  var ENDPOINT = '/api/funnel';

  // The arrival source, snapshotted at parse time. attribution.js clears these
  // params from the address bar once it has stored the touch, so reading them
  // later would see nothing — and the /play page is reached by a decorated CTA
  // carrying the same params, so both pages name the same channel.
  var SOURCE = (function () {
    try {
      var tagged = new URLSearchParams(window.location.search || '').get('utm_source');
      if (tagged) return tagged;
    } catch (e) { /* no URLSearchParams / malformed query */ }
    // A same-tab navigation keeps the touch attribution.js stored; read it, but
    // never write anything back.
    try {
      var held = sessionStorage.getItem('moonfall_attr_first');
      if (held) {
        var parsed = JSON.parse(held);
        if (parsed && parsed.utm_source) return parsed.utm_source;
      }
    } catch (e) { /* storage blocked or junk value */ }
    return '';
  })();

  // One count per milestone per page view. In memory only — a reload is a new
  // visit and counts again, which is what a funnel means by a view.
  var sent = Object.create(null);

  function track(step) {
    if (!step || sent[step]) return;
    sent[step] = true;

    var body;
    try {
      body = JSON.stringify({ step: step, source: SOURCE });
    } catch (e) { return; }

    // sendBeacon survives the unload that a Play click causes; fetch/keepalive
    // is the fallback. Both are fire-and-forget: a funnel count must never
    // delay a navigation or surface an error to the player.
    try {
      if (navigator.sendBeacon) {
        var blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(ENDPOINT, blob)) return;
      }
    } catch (e) { /* fall through to fetch */ }

    try {
      window.fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        keepalive: true,
        credentials: 'omit',
      }).catch(function () { /* counts are best-effort */ });
    } catch (e) { /* no fetch — drop the count */ }
  }

  // The landing page opts in with `data-funnel="landing"` on <body>, so the
  // wiring is greppable from the HTML rather than sniffed from the DOM.
  function autoWire() {
    var page = document.body && document.body.getAttribute('data-funnel');
    if (page !== 'landing') return;
    track('landing_view');
    // Delegated: the hero, nav and ending CTAs are all /play links, and the
    // deck can insert more after this runs.
    document.addEventListener('click', function (event) {
      var target = /** @type {Element|null} */ (event.target);
      var link = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;
      if (!link) return;
      var href = link.getAttribute('href') || '';
      if (href !== '/play' && href.indexOf('/play?') !== 0) return;
      track('landing_play_click');
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoWire, { once: true });
  } else {
    autoWire();
  }

  window.MoonfallFunnel = { track: track, source: function () { return SOURCE; } };
})();
